import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, btnRed, ErrorBox, field, Loading, useLoad } from "@/components/nds/windows/finance/parts";

const Row = ({ k, v, testid }) => <div className="flex gap-3 text-xs"><span className="w-28 shrink-0 text-nex-muted">{k}</span><span className="text-nex-silver break-words" data-testid={testid}>{v}</span></div>;

function Twins({ t, rows }) {
  const twins = rows.filter((x) => t.dup?.ids.includes(x.id));
  if (!twins.length) return null;
  return (
    <div className="p-3 rounded-sm border border-orange-400/40 bg-orange-500/5 space-y-1" data-testid="line-dup-list">
      <div className="text-xs text-orange-200">Possible duplicate · same resident, pay week ending {t.dup.week}</div>
      {twins.map((x) => <div key={x.id} className="text-[11px] text-nex-muted">{money(x.amount)} · pay period {x.pay_period_end || "—"} · {x.origin || x.type_label}</div>)}
    </div>
  );
}

function RemoveForm({ id, paycheck, onChanged, onClose }) {
  const [reason, setReason] = useState("");
  const [pc, setPc] = useState("keep");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await finApi.post("/flow/line/remove", { id, reason, paycheck: paycheck ? pc : "keep" });
      toast.success(`Removed ${r.removed_lines.length} statement line${r.removed_lines.length === 1 ? "" : "s"}`, r.can_restore ? {
        action: { label: "Undo", onClick: () => finApi.post("/flow/line/restore", { id }).then(() => { toast.success("Restored"); onChanged(); }) } } : undefined);
      onChanged();
      onClose();
    } catch (e) { toast.error(finErr(e)); setBusy(false); }
  };
  return (
    <div className="space-y-2 pt-2 border-t border-white/10" data-testid="line-remove-form">
      <div className="text-xs text-nex-silver">Remove this entry (incorrect or duplicate)</div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason, e.g. duplicate of the employer paycheck" data-testid="line-remove-reason" className={`${field} w-full`} />
      {paycheck && <div className="flex flex-wrap gap-2 text-[11px]">{[["keep", "Keep the app paycheck"], ["archive", "Archive the paycheck too"], ["delete", "Delete the paycheck too"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setPc(k)} data-testid={`line-remove-pc-${k}`} className={`px-2 py-1 rounded-sm border ${pc === k ? "border-nex-gold text-nex-gold" : "border-white/10 text-nex-muted"}`}>{l}</button>))}</div>}
      <button type="button" disabled={busy || reason.trim().length < 3} onClick={go} data-testid="line-remove-confirm" className={btnRed}><Trash2 className="inline h-3 w-3 mr-1" />{busy ? "Removing…" : "Remove from report"}</button>
    </div>
  );
}

/** Pop-up for a statement rent / PACT Fee line: where it came from, its possible duplicate, and remove. */
export function LineDialog({ t, rows, onClose, onChanged }) {
  const [state, load] = useLoad(() => finApi.get("/flow/line", { id: t.id }), [t.id]);
  const d = state.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1220] text-nex-silver max-w-lg" data-testid="line-dialog">
        <DialogHeader><DialogTitle className="text-nex-silver">{t.resident_name} · {money(t.amount)}</DialogTitle></DialogHeader>
        {state.loading && !d && <Loading testid="line-loading" />}
        {state.error && <ErrorBox testid="line-error" error={state.error} onRetry={load} />}
        {d && <div className="space-y-3">
          <div className="space-y-1.5">
            <Row k="Came from" v={t.origin || "Statement line"} testid="line-origin" />
            <Row k="Payment" v={t.pay_status_label || "Payment status unknown"} testid="line-paystatus" />
            <Row k="Pay period" v={d.paycheck ? `${d.paycheck.pay_period_start || "?"} → ${d.paycheck.pay_period_end || "?"}` : t.pay_period_end || "—"} testid="line-period" />
            <Row k="Posted" v={t.effective_date} />
            <Row k="Statement" v={`${d.statement.month}${d.statement.locked ? " · locked" : " · not locked"} · line ${d.line.n}${d.line.reference ? ` · #${d.line.reference}` : ""}`} testid="line-statement" />
            <Row k="App paycheck" v={d.paycheck ? `${d.paycheck.employer} · gross ${money(d.paycheck.gross_amount)}${d.paycheck.archived ? " · archived" : ""}` : "No matching paycheck in the app"} testid="line-paycheck" />
            <Row k="Same paycheck" v={d.group.map((x) => `${x.kind} ${money(x.amount)}`).join(" · ")} testid="line-group" />
          </div>
          <Twins t={t} rows={rows} />
          <RemoveForm id={t.id} paycheck={d.paycheck && !d.paycheck.archived} onChanged={onChanged} onClose={onClose} />
          <button type="button" onClick={onClose} data-testid="line-close" className={btnGold}>Close</button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}


/** Links vendor invoice lines to employer paychecks (exact amount, same resident, pay week). Ties are listed, not linked. */
export function MatchInvoices({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await finApi.post("/flow/invoice-match", {});
      toast.success(`Linked ${r.linked} paycheck${r.linked === 1 ? "" : "s"} to vendor invoices · ${r.total_linked} linked in all${r.review.length ? ` · ${r.review.length} need a look` : ""}`);
      setReview(r.review);
      onDone();
    } catch (e) { toast.error(finErr(e)); }
    setBusy(false);
  };
  return (
    <span className="relative">
      <button type="button" onClick={run} disabled={busy} data-testid="flow-match-invoices" className={btnGold}>{busy ? "Matching…" : "Match vendor invoices"}</button>
      {review?.length > 0 && <div data-testid="flow-match-review" className="absolute z-20 mt-1 w-[26rem] max-w-[90vw] max-h-64 overflow-auto p-3 rounded-sm border border-white/70 bg-[#0B1220] space-y-1.5 text-[11px]">
        <div className="flex"><span className="flex-1 text-nex-silver">Not linked · more than one possible match (two invoices, or two paychecks for one invoice line)</span><button type="button" onClick={() => setReview(null)} data-testid="flow-match-review-close" className="text-nex-muted hover:text-nex-gold">Close</button></div>
        {review.map((x) => <div key={x.paycheck_id} className="text-nex-muted">{x.resident} · {x.employer} · {money(x.gross)} · period ending {x.pay_period_end} · invoices {x.candidates.map((c) => `#${c.invoice_number} (${c.status})`).join(", ")}</div>)}
      </div>}
    </span>
  );
}
